import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import LoadingAnimation from "../../components/LoadingAnimation"; // Assuming you have this component
import { IoIosArrowBack } from "react-icons/io";
import { FaFilter } from "react-icons/fa";
import { apiRequestBodyBuilder } from "../config/tableHelper";

const TABLE_COLUMNS = [
  { id: "1", title: "Name", key: "name" },
  { id: "2", title: "Status", key: "status" },
];

const removeDuplicates = (data) => {
  const uniqueData = new Map();
  data.forEach((item) => {
    if (!uniqueData.has(item.id)) {
      uniqueData.set(item.id, item);
    }
  });
  return Array.from(uniqueData.values());
};

const standardizeCampaignData = (data) =>
  data.map((item) => ({
    id: Number(item.campaign_id),
    name: item.campaign_name,
    status: item.status,
  }));

const standardizeKeywordData = (data) =>
  data.map((item) => ({
    id: Number(item.keyword_id),
    name: item.keyword_text,
    status: item.status,
  }));

function EditGroup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const groupId = searchParams.get("groupId");
  const groupType = searchParams.get("groupType") || "campaign";

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [groupName, setGroupName] = useState("");
  const [masterDataList, setMasterDataList] = useState([]); // All available campaigns OR keywords
  const [selectedRows, setSelectedRows] = useState([]); // Array of selected {id, name, status} objects

  const [filterName, setFilterName] = useState("");

  const handleCheckboxChange = (item) => {
    setSelectedRows((prevSelected) => {
      const isSelected = prevSelected.some((row) => row.id === item.id);
      if (isSelected) {
        return prevSelected.filter((row) => row.id !== item.id);
      } else {
        return [...prevSelected, item];
      }
    });
  };

  const handleUpdateGroup = async () => {
    if (!groupName.trim()) {
      setError("Group name cannot be empty.");
      return;
    }
    if (selectedRows.length === 0) {
      setError("You must select at least one item for the group.");
      return;
    }

    // UPDATED: Constructed payload matches new update API requirements
    let payload = apiRequestBodyBuilder(null, {
      group_id: groupId,
      group_name: groupName,
    });

    if (groupType === "campaign") {
      payload.campaign_ids = selectedRows.map((row) => row.id);
      payload.campaign_names = selectedRows.map((row) => row.name);
      payload.group_type = "campaign";
    } else {
      payload.keyword_ids = selectedRows.map((row) => row.id);
      payload.keyword_names = selectedRows.map((row) => row.name);
      payload.group_type = "keyword";
    }

    setLoading(true);
    setError("");

    try {
      // UPDATED: New API Endpoint for Update
      await axios.post(
        "https://api.confidanto.com/campaign-group/update",
        payload,
      );
      // On success, navigate back to the list page
      navigate("/google-ads/group");
    } catch (err) {
      console.error("Update failed:", err);
      setError(
        err.response?.data?.error ||
          "An unexpected error occurred. Failed to update group.",
      );
    } finally {
      setLoading(false);
    }
  };

  const [currentGroupType, setCurrentGroupType] = useState("");

  useEffect(() => {
    if (!groupId) {
      setError("Group ID is missing from the URL.");
      setLoading(false);
      return;
    }

    const fetchGroupDetails = async () => {
      let requestBody = apiRequestBodyBuilder();

      try {
        const res = await axios.post(
          "https://api.confidanto.com/campaign-group2/group-info",
          requestBody,
        );

        const fetchedType = res.data.type;

        setCurrentGroupType(fetchedType);
        console.info(res.data);

        const typeForQuery =
          fetchedType === "keyword" ? "keywords" : fetchedType;

        requestBody.type = typeForQuery;

        return axios.post(
          "https://api.confidanto.com/campaign-group/fetch-groups",
          requestBody,
        );
      } catch (err) {
        console.error("ERROR FETCHING GROUP TYPE", err);
        throw err;
      }
    };

    const fetchMasterData = () => {
      if (groupType === "campaign") {
        return axios.post("https://api.confidanto.com/campaigns/get", {
          project_id: localStorage.getItem("project_id"),
          customer_id: localStorage.getItem("customer_id"),
          email: localStorage.getItem("email"),
          platform: localStorage.getItem("platform"),
          ad_account_id: localStorage.getItem("ad_account_id"),
        });
      } else {
        return axios.post("https://api.confidanto.com/keywords/datewise-data", {
          project_id: localStorage.getItem("project_id"),
          customer_id: localStorage.getItem("customer_id"),
          email: localStorage.getItem("email"),
          platform: localStorage.getItem("platform"),
          ad_account_id: localStorage.getItem("ad_account_id"),
        });
      }
    };

    setLoading(true);
    setError("");

    Promise.all([fetchGroupDetails(), fetchMasterData()])
      .then(([groupsResponse, masterDataResponse]) => {
        const allGroups = Object.values(groupsResponse.data)[0] || [];
        const currentGroup = allGroups.find(
          (group) => group.group_id == groupId,
        );

        if (!currentGroup) {
          setError(`Group with ID "${groupId}" not found.`);
          setLoading(false); // Stop loading if group not found
          return;
        }

        console.log("OUR CURRENT GROUP: ", currentGroup);

        setGroupName(currentGroup.group_name);

        const standardizedMasterData =
          groupType === "campaign"
            ? standardizeCampaignData(masterDataResponse.data)
            : standardizeKeywordData(masterDataResponse.data);

        const uniqueMasterData = removeDuplicates(standardizedMasterData);
        setMasterDataList(uniqueMasterData);
        console.log("OUR CURRENT DATA: ", uniqueMasterData);
        const selectedIds = new Set(
          (groupType === "campaign"
            ? currentGroup.campaign_ids
            : currentGroup.keyword_ids
          ).map(Number),
        );

        console.log("OUR CURRENT IDS: ", selectedIds);

        // Filter the newly standardized list to find the selected items
        const initialSelectedRows = uniqueMasterData.filter((item) =>
          selectedIds.has(Number(item.id)),
        );

        console.log("OUR CURRENT ROWS: ", initialSelectedRows);

        setSelectedRows(initialSelectedRows);
      })
      .catch((err) => {
        console.error("Failed to fetch data:", err);
        setError("Could not load group data. Please try again later.");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [groupId, groupType]);

  const filteredData = useMemo(() => {
    if (!filterName) return masterDataList;
    return masterDataList.filter((item) =>
      item.name?.toLowerCase().includes(filterName.toLowerCase()),
    );
  }, [masterDataList, filterName]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingAnimation />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <Link
          to="/google-ads/group"
          className="flex items-center text-gray-600 hover:text-black"
        >
          <IoIosArrowBack className="h-6 w-6" />
          <span className="ml-1 font-semibold">Back to Groups</span>
        </Link>
      </div>

      {/* Header with actions */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <input
            type="text"
            className="text-lg w-full sm:w-auto flex-grow bg-transparent border-b-2 border-gray-300 p-2 focus:outline-none focus:border-[#4142dc]"
            placeholder="Enter Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
          />
          <div className="flex gap-2">
            <button
              className="bg-[#4142dc] text-white font-semibold px-5 py-2 rounded-md hover:bg-[#3536a8] disabled:bg-gray-400"
              onClick={handleUpdateGroup}
              disabled={!groupName.trim() || selectedRows.length === 0}
            >
              Save Changes
            </button>
            <button
              className="bg-gray-200 text-gray-700 font-semibold px-5 py-2 rounded-md hover:bg-gray-300"
              onClick={() => navigate("/google-ads/group")}
            >
              Cancel
            </button>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-2">
          {selectedRows.length} {groupType}(s) selected
        </p>
        {error && <p className="text-red-500 mt-2 font-semibold">{error}</p>}
      </div>

      {/* Filter and Table Section */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="flex items-center mb-4">
          <FaFilter className="text-gray-500 mr-2" />
          <input
            type="text"
            placeholder={`Filter by ${groupType} name...`}
            value={filterName}
            onChange={(e) => setFilterName(e.target.value)}
            className="w-full sm:w-1/3 border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#4142dc]"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white font-roboto">
            <thead className="bg-[#2930a8] text-white">
              <tr className="text-sm leading-normal">
                <th className="py-3 px-6 text-left w-12"></th>
                {TABLE_COLUMNS.map((col) => (
                  <th key={col.id} className="py-3 px-6 text-left font-medium">
                    {col.key === "name"
                      ? `${
                          groupType.charAt(0).toUpperCase() + groupType.slice(1)
                        } Name`
                      : col.title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-gray-600 text-sm font-light">
              {filteredData.map((item) => {
                const isSelected = selectedRows.some(
                  (row) => row.id === item.id,
                );
                return (
                  <tr
                    key={item.id}
                    className={`border-b border-gray-200 ${
                      isSelected ? "bg-blue-50" : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="py-3 px-6 text-left">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(item)}
                        className="form-checkbox h-4 w-4 text-blue-600"
                      />
                    </td>
                    <td className="py-3 px-6 text-left">{item.name}</td>
                    <td className="py-3 px-6 text-left">
                      <div className="flex items-center">
                        <span
                          className={`h-2.5 w-2.5 rounded-full mr-2 ${
                            item.status === "ENABLED"
                              ? "bg-green-500"
                              : item.status === "PAUSED"
                                ? "bg-gray-500"
                                : item.status === "REMOVED"
                                  ? "bg-red-500"
                                  : "bg-yellow-500"
                          }`}
                        ></span>
                        {item.status || "UNKNOWN"}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredData.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              No {groupType}s found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EditGroup;
